import { useEffect, useRef, useState } from 'preact/hooks';
import { BACKEND_WS, LISTEN_WS_PATH, languageLabel } from '../config';
import { StreamingAudioPlayer } from '../audio';
import type { ServerEvent } from '../types';

interface Props {
  shortCode: string;
  lang: string;
  onLeave: () => void;
}

type Phase = 'gesture' | 'connecting' | 'listening' | 'closed' | 'error';

interface CaptionLine {
  id: number;
  text: string;
  finalized: boolean;
}

export function LiveSession({ shortCode, lang, onLeave }: Props) {
  const [phase, setPhase] = useState<Phase>('gesture');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [listenerCount, setListenerCount] = useState<number>(1);
  const [meta, setMeta] = useState<{ speaker?: string | null; topic?: string | null; sourceLang?: string }>({});
  const [captions, setCaptions] = useState<CaptionLine[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const playerRef = useRef<StreamingAudioPlayer | null>(null);
  const captionIdRef = useRef<number>(0);
  const partialIdRef = useRef<number | null>(null);
  const captionsEndRef = useRef<HTMLDivElement | null>(null);

  function startListening() {
    setPhase('connecting');
    const player = new StreamingAudioPlayer();
    player.start();
    playerRef.current = player;

    const url = `${BACKEND_WS}${LISTEN_WS_PATH}?session_id=${encodeURIComponent(shortCode)}&lang=${encodeURIComponent(lang)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => setPhase('listening');
    ws.onclose = (ev) => {
      if (phase !== 'error') {
        setPhase('closed');
        setErrorMsg(ev.reason || 'Connection closed');
      }
    };
    ws.onerror = () => {
      setPhase('error');
      setErrorMsg('WebSocket error');
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as ServerEvent;
        handleServerEvent(msg);
      } catch {
        /* ignore non-JSON */
      }
    };
  }

  function handleServerEvent(msg: ServerEvent) {
    switch (msg.type) {
      case 'session_meta':
        setMeta({ speaker: msg.speaker_name, topic: msg.topic, sourceLang: msg.source_language });
        break;
      case 'ready':
        // pipeline ready
        break;
      case 'listenerCount':
        setListenerCount(msg.count);
        break;
      case 'partial': {
        const text = msg.text ?? '';
        if (!text) return;
        setCaptions((prev) => {
          const next = [...prev];
          if (partialIdRef.current != null) {
            const idx = next.findIndex((c) => c.id === partialIdRef.current);
            if (idx >= 0) next[idx] = { ...next[idx], text };
            else { partialIdRef.current = null; }
          }
          if (partialIdRef.current == null) {
            const id = ++captionIdRef.current;
            partialIdRef.current = id;
            next.push({ id, text, finalized: false });
          }
          return next.slice(-200);
        });
        break;
      }
      case 'final': {
        setCaptions((prev) => {
          const next = [...prev];
          if (partialIdRef.current != null) {
            const idx = next.findIndex((c) => c.id === partialIdRef.current);
            if (idx >= 0) {
              next[idx] = { ...next[idx], text: msg.text ?? next[idx].text, finalized: true };
              partialIdRef.current = null;
              return next.slice(-200);
            }
          }
          const id = ++captionIdRef.current;
          next.push({ id, text: msg.text ?? '', finalized: true });
          return next.slice(-200);
        });
        break;
      }
      case 'audio': {
        const b64 = msg.mp3Base64 ?? msg.pcmBase64;
        if (b64 && playerRef.current) playerRef.current.enqueueBase64(b64);
        break;
      }
      case 'complete':
        setPhase('closed');
        setErrorMsg('Broadcast ended by speaker');
        break;
      case 'error':
        setPhase('error');
        setErrorMsg(msg.message);
        break;
    }
  }

  useEffect(() => {
    return () => {
      try { wsRef.current?.close(); } catch { /* */ }
      playerRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    captionsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [captions.length]);

  if (phase === 'gesture') {
    return (
      <main style={mainStyle}>
        <Header shortCode={shortCode} lang={lang} meta={meta} onLeave={onLeave} listenerCount={listenerCount} />
        <div style={{ marginTop: 'auto', marginBottom: 'auto', textAlign: 'center', padding: 24 }}>
          <div style={{ fontSize: 64 }}>🎧</div>
          <h2 style={{ fontSize: 22, margin: '12px 0 8px' }}>按此開始聆聽</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 15, margin: '0 auto', maxWidth: 320 }}>
            Tap below to enable audio. Mobile browsers require a tap before playing sound.
          </p>
          <button onClick={startListening} style={primaryBtn}>Start listening</button>
        </div>
      </main>
    );
  }

  return (
    <main style={mainStyle}>
      <Header shortCode={shortCode} lang={lang} meta={meta} onLeave={onLeave} listenerCount={listenerCount} />

      <div style={{ padding: '8px 16px' }}>
        <StatusBar phase={phase} errorMsg={errorMsg} />
      </div>

      <section style={captionsContainer}>
        {captions.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: 40 }}>
            Waiting for the speaker…
          </div>
        ) : (
          captions.map((c) => (
            <p key={c.id} style={{
              margin: '0 0 14px', fontSize: 22, lineHeight: 1.45,
              color: c.finalized ? 'var(--text)' : 'var(--text-muted)',
              fontWeight: c.finalized ? 500 : 400,
            }}>
              {c.text}
            </p>
          ))
        )}
        <div ref={captionsEndRef} />
      </section>

      {(phase === 'closed' || phase === 'error') && (
        <div style={{ padding: 16 }}>
          <button onClick={onLeave} style={primaryBtn}>Back to languages</button>
        </div>
      )}
    </main>
  );
}

function Header({ shortCode, lang, meta, onLeave, listenerCount }: {
  shortCode: string; lang: string; meta: { speaker?: string | null; topic?: string | null; sourceLang?: string }; onLeave: () => void; listenerCount: number;
}) {
  return (
    <header style={headerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <button onClick={onLeave} style={{ background: 'transparent', border: 0, color: 'var(--accent)', fontSize: 15, padding: 8, cursor: 'pointer' }}>← Back</button>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 10 }}>
          <span>👥 {listenerCount}</span>
          <span>·</span>
          <span>{shortCode}</span>
        </div>
      </div>
      <div style={{ padding: '4px 16px 12px' }}>
        <div style={{ fontSize: 12, letterSpacing: 1.5, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
          Live · {languageLabel(meta.sourceLang ?? '')} → {languageLabel(lang)}
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>
          {meta.topic ?? 'Live Broadcast'}
        </div>
        {meta.speaker ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>by {meta.speaker}</div>
        ) : null}
      </div>
    </header>
  );
}

function StatusBar({ phase, errorMsg }: { phase: Phase; errorMsg: string }) {
  if (phase === 'connecting') {
    return <BadgeRow color="warn" label="Connecting…" />;
  }
  if (phase === 'listening') {
    return <BadgeRow color="success" label="Live" pulse />;
  }
  if (phase === 'closed') {
    return <BadgeRow color="muted" label={`Ended · ${errorMsg}`} />;
  }
  if (phase === 'error') {
    return <BadgeRow color="danger" label={errorMsg || 'Connection error'} />;
  }
  return null;
}

function BadgeRow({ color, label, pulse }: { color: 'warn' | 'success' | 'muted' | 'danger'; label: string; pulse?: boolean }) {
  const dot: Record<string, string> = { warn: 'var(--warn)', success: 'var(--success)', muted: 'var(--text-muted)', danger: 'var(--danger)' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)' }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: dot[color],
        animation: pulse ? 'pulse 1.4s ease-in-out infinite' : 'none',
      }} />
      <span>{label}</span>
      <style>{`@keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }`}</style>
    </div>
  );
}

const mainStyle: any = { display: 'flex', flexDirection: 'column', minHeight: '100dvh' };
const headerStyle: any = {
  position: 'sticky', top: 0, zIndex: 5,
  background: 'rgba(14,17,22,0.92)', backdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--border)',
};
const captionsContainer: any = {
  flex: 1, overflowY: 'auto', padding: '20px 20px 40px', maxWidth: 720, width: '100%', margin: '0 auto',
};
const primaryBtn: any = {
  marginTop: 24, padding: '16px 28px', fontSize: 17, fontWeight: 600,
  background: 'var(--accent)', color: '#fff', border: 0, borderRadius: 12, cursor: 'pointer',
  width: '100%', maxWidth: 320,
};
